import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Select,
  useToast
} from "@chakra-ui/react";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosInstance";
import CustomDateRangePicker from "../Customer-dashboard/CustomDateRangePicker";
import OrdersTable from "../Customer-dashboard/ReceptionistOrders/OrdersTable";
import PaginationControls from "../Customer-dashboard/ReceptionistOrders/PaginationControls";

const filterOptions = ["Today", "Last 7 Days", "Last 30 Days", "Custom Date"];

const OrderInventory = () => {
  const toast = useToast();

  const [activeFilter, setActiveFilter] = useState("Last 30 Days");
  const [dateRange, setDateRange] = useState({ fromDate: null, toDate: null });

  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    status: [],
    paymentStatus: "",
    receptionistId: "",
    tailorId: "",
    search: "",
  });



  const getDates = useCallback((option) => {
    const today = dayjs().startOf("day");
    switch (option) {
      case "Today":
        return {
          fromDate: today.toISOString(),
          toDate: today.endOf("day").toISOString(),
        };
      case "Last 7 Days":
        return {
          fromDate: today.subtract(7, "day").toISOString(),
          toDate: today.endOf("day").toISOString(),
        };
      case "Last 30 Days":
        return {
          fromDate: today.subtract(30, "day").toISOString(),
          toDate: today.endOf("day").toISOString(),
        };
      default:
        return { fromDate: null, toDate: null };
    }
  }, []);

  const handleFilterClick = (option) => {
    setActiveFilter(option);
    setPage(1);
    setDateRange(getDates(option));
  };

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axiosInstance.get(
        "/api/users/roles/list?roles=master,receptionist"
      );
      setUsers(res.data || []);
    } catch (err) {
      toast({ title: "Failed to load users", status: "error" });
    }
  }, [toast]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/api/orders", {
        params: {
          page,
          limit: pageSize,
          fromDate: dateRange.fromDate,
          toDate: dateRange.toDate,
          ...filters,
          status: filters.status.join(","),
        },
      });
      // OrdersTable expects a flat array of orders (not grouped)
      setOrders(res.data.orders || []);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      toast({ title: "Failed to load orders", status: "error" });
    } finally {
      setLoading(false);
    }
  }, [dateRange, filters, page, pageSize, toast]);

 

  

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleInputChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };




  return (
    <Box p={{ base: 4, md: 6 }} bg="gray.50" minH="100vh">
      <HStack spacing={2} wrap="wrap" mb={4}>
        {filterOptions.map((option) => (
          <Button
            key={option}
            size="sm"
            variant={activeFilter === option ? "solid" : "outline"}
            colorScheme="orange"
            onClick={() => handleFilterClick(option)}
          >
            {option}
          </Button>
        ))}
      </HStack>
      {activeFilter === "Custom Date" && (
        <Box mb={8}>
          <CustomDateRangePicker
            onApply={({ fromDate, toDate }) =>
              setDateRange({
                fromDate: dayjs(fromDate).startOf("day").toISOString(),
                toDate: dayjs(toDate).endOf("day").toISOString(),
              })
            }
          />
        </Box>
      )}
      <Flex gap={2} flexWrap="wrap" mb={4}>
        <Select
          placeholder="Select Master"
          size="sm"
          onChange={(e) => handleInputChange("tailorId", e.target.value)}
        >
          {users
            .filter((u) => u.role === "master")
            .map((u) => (
              <option key={u._id} value={u._id}>
                {u.name}
              </option>
            ))}
        </Select>
        <Select
          placeholder="Select Receptionist"
          size="sm"
          onChange={(e) => handleInputChange("receptionistId", e.target.value)}
        >
          {users
            .filter((u) => u.role === "receptionist")
            .map((u) => (
              <option key={u._id} value={u._id}>
                {u.name}
              </option>
            ))}
        </Select>
        <Select
          placeholder="Status"
          size="sm"
          onChange={(e) => handleInputChange("status", [e.target.value])}
        >
          <option value="in-progress">In Progress</option>
          <option value="done">Done</option>
          <option value="cancel">Cancelled</option>
        </Select>
        <Select
          placeholder="Payment Status"
          size="sm"
          onChange={(e) => handleInputChange("paymentStatus", e.target.value)}
        >
          <option value="unpaid">Unpaid</option>
          <option value="partial">Partial</option>
          <option value="paid">Paid</option>
        </Select>
        <Input
          size="sm"
          placeholder="Search by Phone"
          onChange={(e) => handleInputChange("search", e.target.value)}
        />
      </Flex>
      <Box bg="white" borderRadius="md" shadow="sm" p={4} overflowX="auto">
        <OrdersTable
          data={orders}
          fetchOrders={fetchOrders}
          orderLoading={loading}
        />
        <PaginationControls
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </Box>
    </Box>
  );
};

export default OrderInventory;
